// src/components/VoteButtons.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import LoginModal from './auth/LoginModal';

const VoteButtons = ({ game, onVote, showLogin, vertical = false }) => {
  const { currentUser } = useAuth();
  const [isVoting, setIsVoting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingVoteValue, setPendingVoteValue] = useState(null);

  // Determine if the user has already voted on this game
  const userVote = game.userVote || 0; // 1 for upvote, -1 for downvote, 0 for no vote

  // src/components/VoteButtons.js

const handleVote = async (e, voteValue) => {
  e.stopPropagation();
  
  // If not logged in, show login modal
  if (!currentUser) {
    console.log("User not logged in, showing login with vote value:", voteValue);
    
    // For the global login modal
    if (typeof showLogin === 'function') {
      showLogin(() => {
        console.log("Executing vote callback after login with value:", voteValue);
        // Simple approach - just call the vote API directly here
        voteForGame(game.id, voteValue);
      });
    } else {
      // For the local login modal fallback
      setPendingVoteValue(voteValue);
      setShowLoginModal(true);
    }
    return;
  }

  // User is already logged in, handle vote directly
  const newVote = userVote === voteValue ? 0 : voteValue;
  handleVoteAction(newVote);
};

// Add a new helper function that directly calls the API:
const voteForGame = async (gameId, voteValue) => {
  try {
    setIsVoting(true);
    // Calculate the new vote value
    const newVote = userVote === voteValue ? 0 : voteValue;
    
    console.log("Making vote API call with value:", newVote);
    const response = await fetch(`/api/games/${gameId}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vote: newVote }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to vote');
    }

    // Update the UI with the new vote counts
    const data = await response.json();
    if (onVote) {
      onVote(gameId, newVote, data.upvotes, data.downvotes);
    }
  } catch (error) {
    console.error('Voting error:', error);
  } finally {
    setIsVoting(false);
  }
};

  // Helper function to handle the actual voting action
const handleVoteAction = async (newVote) => {
  try {
    setIsVoting(true);
    const response = await fetch(`/api/games/${game.id}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vote: newVote }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to vote');
    }

    // Update the UI with the new vote counts
    const data = await response.json();
    if (onVote) {
      // Handle both regular game IDs and recommendation game_ids
      const gameId = game.id || game.game_id;
      onVote(gameId, newVote, data.upvotes, data.downvotes);
    }
  } catch (error) {
    console.error('Voting error:', error);
  } finally {
    setIsVoting(false);
  }
};

   
  
  return (
    <>
      <div className={`flex ${vertical ? 'flex-col space-y-1' : 'items-center space-x-2'}`}>
        <button
          onClick={(e) => handleVote(e, 1)}
          disabled={isVoting}
          className={`flex ${vertical ? 'flex-col items-center' : 'items-center'} p-1 rounded focus:outline-none transition-colors ${
            userVote === 1
              ? 'text-green-400'
              : 'text-gray-200 hover:text-green-400'
          }`}
          title="Upvote"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"
            />
          </svg>
          <span className={`${vertical ? "text-xs mt-0.5" : "ml-1"} font-medium`}>{game.upvotes || 0}</span>
        </button>

        <button
          onClick={(e) => handleVote(e, -1)}
          disabled={isVoting}
          className={`flex ${vertical ? 'flex-col items-center' : 'items-center'} p-1 rounded focus:outline-none transition-colors ${
            userVote === -1
              ? 'text-red-400'
              : 'text-gray-200 hover:text-red-400'
          }`}
          title="Downvote"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z"
            />
          </svg>
          <span className={`${vertical ? "text-xs mt-0.5" : "ml-1"} font-medium`}>{game.downvotes || 0}</span>
        </button>
      </div>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        onLoginSuccess={() => {
          console.log("Local login modal success, pending vote:", pendingVoteValue);
          if (pendingVoteValue !== null) {
            voteForGame(game.id, pendingVoteValue);
            setPendingVoteValue(null);
          }
        }}
      />
    </>
  );
};

export default VoteButtons;